/**
 * Finding a row by anything on it (G2, Freund 2026-09-23).
 *
 * Every search box in this app matched ONE field — usually a name. Freund went
 * looking for a transaction by its amount, the box could not do it, and he
 * fell back to the browser's own Ctrl-F, which only ever sees the hundred rows
 * already rendered. `184_30m40s.jpg` is that happening: `13,956.88` typed into
 * Opera's find bar over a page whose own search box was labelled "Search
 * counterparty".
 *
 * ⚠️ **THE COMMAS ARE THE WHOLE PROBLEM AND THEY ARE WHY THIS FILE EXISTS.**
 *
 * He typed `13,956.88`. The database holds `13956.88`. A naive `includes`
 * finds nothing, reports "no results", and the reader concludes the
 * transaction is missing from the system — which is a far more expensive wrong
 * answer than "your search box is limited". A search that cannot find a figure
 * written the way a person writes figures is a search that manufactures
 * phantom data-loss reports.
 *
 * So every comparison happens twice: once on the text as typed, and once with
 * both sides stripped of the characters people put in money and dates but
 * databases do not. `$13,956.88`, `13956.88` and `13,956.88` all have to find
 * the same row, and so do `9/23` and `09-23`.
 *
 * ---------------------------------------------------------------------------
 * ⚠️ THE DATE HALF OF THAT PROMISE WAS NOT TRUE UNTIL 2026-09-24.
 *
 * `loosen` stripped whitespace, commas, dollars and brackets -- and NOT `/` or
 * `-`. So the sentence above was describing behaviour the code did not have.
 * Measured, not inferred:
 *
 *     "9/23"       finds "09-23"                  -> false
 *     "9/23/26"    finds a row dated 2026-09-23   -> false
 *     "09/23/2026" finds a row dated 2026-09-23   -> false
 *     "2026-09-23" finds it                       -> true
 *
 * Rows carry dates as ISO, because that is what the database holds and what
 * gets rendered. Nobody types a date that way. So every search box in the app
 * -- all of which advertise themselves as searching every column, amounts and
 * dates included -- answered "no results" to a date written the way a person
 * writes one, and the reader concludes the row is not there. That is the same
 * phantom-data-loss failure the commas were fixed for, one column over. Sarah,
 * via Jonathan, 2026-09-24: *"the dates for chart of accounts search isn't
 * working."*
 *
 * The fix is a THIRD pass, not a change to the first two: a word that reads as
 * a date is also tried in its ISO form. `lib/dates.ts` already had to learn to
 * read "92326" for the filter fields, so the search and the date filters now
 * agree on what a typed date means, which is worth more than either fix alone.
 * ---------------------------------------------------------------------------
 */
import { parseLooseDate } from "./dates";

/**
 * Lowercased, with the separators people type but data does not carry.
 *
 * ⚠️ `/` and `-` are in here now. They have to be, for `9/23` to find `09-23`
 * at all -- but on their own they are not enough for the case that actually
 * gets typed: loosening turns `9/23/26` into `92326` and `2026-09-23` into
 * `20260923`, which do not contain one another. Re-ordering a date is a job
 * for a date parser, and that is the third pass in `matches`.
 */
function loosen(value: string): string {
  return value.toLowerCase().replace(/[\s,$()/-]/g, "");
}

/**
 * The ISO form of a word that reads as a date, or null.
 *
 * ⚠️ DELIBERATELY NARROWER THAN `parseLooseDate`. A search box is full of
 * money, and `parseLooseDate` will happily read `1.5` as the 5th of January --
 * which would make a search for an amount start matching rows by date. So:
 *
 *   * anything with a decimal point is money, never a date;
 *   * bare digits need at least five of them, because `923` as an amount is
 *     far more likely than `923` as a date -- and the loosened pass already
 *     finds `923` inside `20260923` anyway, so nothing is lost by refusing;
 *   * everything else (`9/23/26`, `9-23-2026`, `2026-09-23`) is date-shaped
 *     enough to be worth a try.
 *
 * A false negative here costs a search result. A false positive silently mixes
 * date matches into an amount search, which is the kind of quietly-wrong
 * answer this file exists to prevent -- so it errs toward refusing.
 */
function asDate(word: string): string | null {
  if (word.includes(".")) return null;
  if (/^\d+$/.test(word) && word.length < 5) return null;
  return parseLooseDate(word);
}

/**
 * Everything a row can be found by, as one string.
 *
 * Pass the DISPLAYED values as well as the raw ones where they differ — a
 * pill reading "active", a formatted `$1,234.56`, a parent path. If the reader
 * can see it in the row, typing it should find the row; anything less and they
 * are back to guessing which column the box happens to look at.
 */
export function haystack(
  parts: Array<string | number | null | undefined>,
): string {
  return parts
    .filter((p) => p !== null && p !== undefined && p !== "")
    .join(" \u0000 ")
    .toLowerCase();
}

/**
 * Does this row match what was typed?
 *
 * Every whitespace-separated word must appear somewhere in the row, in any
 * field and in any order — so "chase 1246" finds the Chase account with that
 * balance without the reader knowing which column holds which. An empty needle
 * matches everything, which is what makes this safe to call unconditionally.
 *
 * ⚠️ The loosened pass is a SECOND chance, never a replacement. Stripping
 * commas from both sides makes `1,234` find `1234`, but it would also make
 * `12,34` find `1234`, so the strict pass runs first and the loose one only
 * catches what it missed. Both are substring tests; neither is fuzzy.
 */
export function matches(hay: string, needle: string): boolean {
  const query = needle.trim().toLowerCase();
  if (!query) return true;
  const loose = loosen(hay);
  return query.split(/\s+/).every((word) => {
    if (hay.includes(word) || loose.includes(loosen(word))) return true;
    // Third chance: the word read as a date, in the form the rows carry.
    const iso = asDate(word);
    return iso !== null && hay.includes(iso);
  });
}

/**
 * The two of them together, for the common case.
 *
 * `useMemo` over the row list at the call site, not here: building a haystack
 * per row per keystroke is fine at the few hundred rows these tables hold and
 * is not fine at the few thousand the books side will reach.
 */
export function rowMatches(
  parts: Array<string | number | null | undefined>,
  needle: string,
): boolean {
  if (!needle.trim()) return true;
  return matches(haystack(parts), needle);
}
